import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { API_BASE } from './api';
import useBackdropClose from './useBackdropClose';
import { toolbarButton } from './toolbarButton';

// Must match REPORT_REASONS in backend/models/Report.js.
export const REPORT_REASONS = [
  'Scam or fraud',
  'Illegal goods or services',
  'Harassment or hate',
  'Violence or danger',
  'Spam or misleading',
  'Other',
];

// "Report" link + dialog for an event (EU Digital Services Act notice and action).
// Opens by itself when the page URL ends in #report (used by invitation emails).
export default function ReportEvent({ slug, token }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const close = () => setOpen(false);
  const backdropHandlers = useBackdropClose(close);

  useEffect(() => {
    if (window.location.hash === '#report') setOpen(true);
  }, []);

  useEffect(() => {
    if (open) {
      setReason('');
      setDetails('');
      setError('');
      setSent(false);
    }
  }, [open]);

  const submit = async (event) => {
    event.preventDefault();
    if (!reason) {
      setError('Choose a reason.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/events/${encodeURIComponent(slug)}/report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ reason, details }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Couldn’t send the report.');
      setSent(true);
    } catch (submitError) {
      setError(submitError.message || 'Couldn’t send the report.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} style={styles.link}>
        Report
      </button>

      {open && ReactDOM.createPortal(
        <div style={styles.overlay} {...backdropHandlers} role="presentation">
          <div style={styles.dialog} role="dialog" aria-modal="true" aria-labelledby="report-title" onClick={(event) => event.stopPropagation()}>
            <h3 id="report-title" style={styles.title}>Report this event</h3>

            {sent ? (
              <>
                <p style={styles.copy}>Thanks. We’ll review it. Reports are anonymous.</p>
                <div style={styles.actions}>
                  <button type="button" onClick={close} style={toolbarButton}>Close</button>
                </div>
              </>
            ) : (
              <form onSubmit={submit}>
                <p style={styles.copy}>What’s wrong with it? Reports are anonymous.</p>
                <div role="radiogroup" aria-label="Reason" style={styles.reasons}>
                  {REPORT_REASONS.map((item) => (
                    <label key={item} style={styles.reason}>
                      <input
                        type="radio"
                        name="report-reason"
                        value={item}
                        checked={reason === item}
                        onChange={() => setReason(item)}
                      />
                      {item}
                    </label>
                  ))}
                </div>
                <textarea
                  value={details}
                  onChange={(event) => setDetails(event.target.value)}
                  placeholder="Anything else we should know? (optional)"
                  aria-label="Details"
                  maxLength={1000}
                  rows={3}
                  style={styles.details}
                />
                {error && <p role="alert" style={styles.error}>{error}</p>}
                <div style={styles.actions}>
                  <button type="button" onClick={close} disabled={busy} style={toolbarButton}>Cancel</button>
                  <button type="submit" disabled={busy || !reason} style={{ ...toolbarButton, opacity: busy || !reason ? 0.5 : 1 }}>
                    {busy ? 'Sending…' : 'Send report'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

const styles = {
  link: {
    background: 'none',
    border: 'none',
    padding: 0,
    color: '#8c7059',
    textDecoration: 'underline',
    cursor: 'pointer',
  },
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 2000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    background: 'rgba(0,0,0,0.35)',
  },
  dialog: {
    width: 'min(440px, 92vw)',
    background: '#fff',
    borderRadius: 6,
    padding: 20,
    boxSizing: 'border-box',
    boxShadow: '0 12px 30px rgba(0,0,0,0.15)',
  },
  title: {
    margin: '0 0 8px',
  },
  copy: {
    margin: '0 0 12px',
    color: '#4b5563',
  },
  reasons: {
    display: 'grid',
    gap: 6,
    marginBottom: 12,
  },
  reason: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    cursor: 'pointer',
  },
  details: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '8px 10px',
    border: '1px solid #ccc',
    borderRadius: 3,
  },
  error: {
    margin: '8px 0 0',
    color: '#b42318',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: 12,
  },
};
